// App-shell enforcement banner for the SaaS billing prototype (#06).
// See plans/saas-billing/06-payment-enforcement-dunning.md.
//
// Mounted in app.component.html above the router-outlet so it appears on
// authenticated routes. Billing data loads once per login session, not on
// every navigation. The banner is hidden on /billing (the hub page owns its
// own banner) and on pre-auth surfaces.
//
// Severity ramp:
//   - past_due + daysRemaining >= 7 -> yellow
//   - past_due + 3 <= daysRemaining < 7 -> orange
//   - past_due + 0 < daysRemaining < 3 -> red
//   - restricted (unpaid OR past_due day 0) -> red, non-dismissible
//
// Dismiss key (sessionStorage): {tenantId}:{gracePeriodEndsAt}:{severity}
// A new severity ramp invalidates the key so the banner re-surfaces.
//
// Always-allowed routes: enumerated as a constant so reviewers can see the
// exempt list at a glance. These never trigger the feature-gate modal even
// when enforcement is restricted.

import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, from, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { BillingPermissions } from '../../../app-configs/billing/permissions';
import { EnforcementStatus, Subscription } from '../../../models/billing';
import { AuthService } from '../../../services/auth.service';
import {
  CurrentUser,
  IBillingMockService
} from '../../../services/billing-mock/billing-mock.interface';
import { BILLING_SERVICE } from '../../../services/billing-mock/billing.token';
import { LoggerService } from '../../../services/logger.service';
import { PermissionService } from '../../../services/permission.service';

// Note: real route-level enforcement (a RouteGuardService that blocks
// navigation to restricted-feature routes when `level==='restricted'`) is
// deferred to backend impl. The prototype demonstrates gating via the /billing
// demo buttons only.

/**
 * Routes on which the app-shell banner is SUPPRESSED. /billing is suppressed
 * because #03 renders its own past-due / restricted / cancelled banner; the
 * auth/signup routes are pre-auth surfaces with no enforcement context.
 */
const BANNER_SUPPRESS_PREFIXES: ReadonlyArray<string> = [
  '/billing',
  '/signup',
  '/login',
  '/verify',
  '/device-verify',
  '/factor-setup',
  '/dev-auth',
  '/d',
  '/not-found',
  '/auth',
  '/logout'
];

const EMPTY_ENFORCEMENT: EnforcementStatus = {
  level: 'none',
  restrictedFeatures: [],
  ctaUrl: '/billing'
};

const STAFF_CURRENT_USER: CurrentUser = {
  id: '',
  canManageBilling: false
};

interface BannerViewModel {
  show: boolean;
  level: 'past_due' | 'restricted';
  severity: 'yellow' | 'orange' | 'red';
  dismissible: boolean;
  deadlineLabel: string;
  daysLabel: string;
  isAdmin: boolean;
  /**
   * Severity-specific past-due body copy (yellow/orange/red). Built once when
   * the view model is computed so the template doesn't call helpers that
   * return new strings on every change-detection cycle.
   */
  pastDueBody: string;
}

@Component({
    selector: 'app-billing-enforcement-banner',
    imports: [CommonModule],
    template: `
    @if (viewModel$ | async; as vm) {
      @if (vm.show) {
        <div
          class="enforcement-banner"
          [class.enforcement-banner--yellow]="vm.severity === 'yellow' && vm.level === 'past_due'"
          [class.enforcement-banner--orange]="vm.severity === 'orange' && vm.level === 'past_due'"
          [class.enforcement-banner--red]="vm.severity === 'red' && vm.level === 'past_due'"
          [class.enforcement-banner--restricted]="vm.level === 'restricted'"
          role="alert"
          data-testid="enforcement-banner">
          <div class="enforcement-banner__icon" aria-hidden="true">
            {{ vm.level === 'restricted' ? '!' : '!' }}
          </div>
          <div class="enforcement-banner__body">
            @if (!vm.isAdmin) {
              <div class="enforcement-banner__title" data-testid="enforcement-banner-title">
                Your organization&rsquo;s subscription needs attention.
              </div>
              <div class="enforcement-banner__detail" data-testid="enforcement-banner-detail">
                Please contact your account admin to update payment.
              </div>
            } @else {
              @if (vm.level === 'restricted') {
                <div class="enforcement-banner__title" data-testid="enforcement-banner-title">
                  Your account access is restricted
                </div>
                <div class="enforcement-banner__detail" data-testid="enforcement-banner-detail">
                  Update your payment method to restore access.
                </div>
                <div
                  class="enforcement-banner__secondary"
                  data-testid="enforcement-banner-secondary">
                  AI chat and new record creation are paused. You can still view and
                  export your data.
                </div>
              } @else {
                <div class="enforcement-banner__title" data-testid="enforcement-banner-title">
                  We couldn&rsquo;t process your last payment.
                </div>
                <div class="enforcement-banner__detail" data-testid="enforcement-banner-detail">
                  {{ vm.pastDueBody }}
                </div>
              }
            }
          </div>
          <div class="enforcement-banner__actions">
            @if (vm.isAdmin) {
              <button
                type="button"
                class="enforcement-banner__cta"
                (click)="onCtaClick()"
                data-testid="enforcement-banner-cta">
                Update payment method &#8599;
              </button>
            }
            @if (vm.dismissible && vm.isAdmin) {
              <button
                type="button"
                class="enforcement-banner__dismiss"
                (click)="onDismiss()"
                data-testid="enforcement-banner-dismiss">
                Dismiss
              </button>
            }
          </div>
        </div>
      }
    }
    `,
    styles: [
        `
      :host {
        display: block;
        /* ion-app is an ion-page (column, space-between) and ion-router-outlet
           covers that page at z-index 0. Stay at the top, above the outlet. */
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 10;
        pointer-events: none;
      }
      .enforcement-banner {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        pointer-events: auto;
        padding: 12px 16px;
        border-left: 4px solid transparent;
        font-size: 13px;
      }
      .enforcement-banner__icon {
        flex: 0 0 24px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        text-align: center;
        line-height: 24px;
        font-weight: 700;
        color: #ffffff;
      }
      .enforcement-banner__body {
        flex: 1;
        min-width: 0;
      }
      .enforcement-banner__title {
        font-weight: 600;
        margin-bottom: 2px;
      }
      .enforcement-banner__detail {
        font-size: 12px;
        opacity: 0.85;
      }
      .enforcement-banner__secondary {
        font-size: 11px;
        opacity: 0.7;
        margin-top: 4px;
      }
      .enforcement-banner__actions {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .enforcement-banner__cta,
      .enforcement-banner__dismiss {
        font-size: 12px;
        padding: 6px 12px;
        border-radius: 5px;
        border: none;
        cursor: pointer;
        font-weight: 500;
      }
      .enforcement-banner__dismiss {
        background: transparent;
        border: 1px solid currentColor;
        opacity: 0.7;
      }
      .enforcement-banner__dismiss:hover {
        opacity: 1;
      }
      .enforcement-banner--yellow {
        background: #fefce8;
        border-left-color: #ca8a04;
        color: #713f12;
      }
      .enforcement-banner--yellow .enforcement-banner__icon {
        background: #eab308;
      }
      .enforcement-banner--yellow .enforcement-banner__cta {
        background: #ca8a04;
        color: #ffffff;
      }
      .enforcement-banner--yellow .enforcement-banner__cta:hover {
        background: #a16207;
      }
      .enforcement-banner--orange {
        background: #fff7ed;
        border-left-color: #ea580c;
        color: #7c2d12;
      }
      .enforcement-banner--orange .enforcement-banner__icon {
        background: #f97316;
      }
      .enforcement-banner--orange .enforcement-banner__cta {
        background: #ea580c;
        color: #ffffff;
      }
      .enforcement-banner--orange .enforcement-banner__cta:hover {
        background: #c2410c;
      }
      .enforcement-banner--red {
        background: #fef2f2;
        border-left-color: #dc2626;
        color: #7f1d1d;
      }
      .enforcement-banner--red .enforcement-banner__icon {
        background: #dc2626;
      }
      .enforcement-banner--red .enforcement-banner__cta {
        background: #dc2626;
        color: #ffffff;
      }
      .enforcement-banner--red .enforcement-banner__cta:hover {
        background: #b91c1c;
      }
      .enforcement-banner--restricted {
        background: #fef2f2;
        border-left-color: #b91c1c;
        color: #7f1d1d;
        border-left-width: 6px;
      }
      .enforcement-banner--restricted .enforcement-banner__icon {
        background: #b91c1c;
      }
      .enforcement-banner--restricted .enforcement-banner__cta {
        background: #b91c1c;
        color: #ffffff;
      }
      .enforcement-banner--restricted .enforcement-banner__cta:hover {
        background: #991b1b;
      }
      @media (max-width: 520px) {
        .enforcement-banner {
          flex-wrap: wrap;
        }
        .enforcement-banner__actions {
          flex-basis: 100%;
          justify-content: flex-end;
        }
      }
    `
    ]
})
export class EnforcementBannerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /**
   * Tracks dismiss state keyed by `{tenantId}:{gracePeriodEndsAt}:{severity}`.
   * Stored in sessionStorage so the banner reappears on next tab/load. When
   * sessionStorage is unavailable (private browsing, SSR), falls back to a
   * BehaviorSubject so the component still functions (just doesn't persist).
   */
  private readonly dismissedKey$ = new BehaviorSubject<string | null>(null);

  private readonly currentUrl$ = new BehaviorSubject<string>('');

  viewModel$!: Observable<BannerViewModel>;

  private currentStatus: EnforcementStatus | null = null;
  private currentSubscription: Subscription | null = null;
  private currentUser: CurrentUser | null = null;
  /** Latest show intent, so a late measurement cannot reopen a hidden banner. */
  private shellBannerShown = false;

  constructor(
    @Inject(BILLING_SERVICE) private readonly billing: IBillingMockService,
    private readonly router: Router,
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly permissions: PermissionService
  ) {}

  ngOnInit(): void {
    // Load any persisted dismiss key from sessionStorage (per-tab persistence,
    // per spec). Failures are non-fatal.
    try {
      if (typeof sessionStorage !== 'undefined') {
        const stored = sessionStorage.getItem('billing.enforcement.dismissed');
        if (stored !== null) {
          this.dismissedKey$.next(stored);
        }
      }
    } catch (error) {
      this.logger.error(
        'EnforcementBannerComponent',
        'ngOnInit',
        'failed to read dismiss key from sessionStorage',
        { error }
      );
    }

    // URL only controls whether the banner is visible. Billing data loads once
    // per authenticated session, not on every NavigationEnd.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        this.currentUrl$.next(event.urlAfterRedirects);
      });
    this.currentUrl$.next(this.router.url);

    const authenticated$ = this.authService.authSession$.pipe(
      map(session => !!session.token),
      distinctUntilChanged()
    );

    const canViewBilling$ = authenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated
          ? from(this.permissions.has(BillingPermissions.AccountView)).pipe(catchError(() => of(false)))
          : of(false)
      ),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      shareReplay(1)
    );
    const status$ = authenticated$.pipe(
      switchMap(isAuthenticated =>
        isAuthenticated
          ? this.billing.getEnforcementStatus().pipe(catchError(() => of(EMPTY_ENFORCEMENT)))
          : of(EMPTY_ENFORCEMENT)
      ),
      takeUntil(this.destroy$),
      shareReplay(1)
    );
    const subscription$ = canViewBilling$.pipe(
      switchMap(canViewBilling =>
        canViewBilling
          ? this.billing.getCurrentSubscription().pipe(catchError(() => of(null as Subscription | null)))
          : of(null as Subscription | null)
      ),
      takeUntil(this.destroy$),
      shareReplay(1)
    );
    const user$ = canViewBilling$.pipe(
      switchMap(canViewBilling =>
        canViewBilling
          ? this.billing.getCurrentUser().pipe(catchError(() => of(null as CurrentUser | null)))
          : of(STAFF_CURRENT_USER)
      ),
      takeUntil(this.destroy$),
      shareReplay(1)
    );

    this.viewModel$ = combineLatest([
      status$,
      subscription$,
      user$,
      this.currentUrl$,
      this.dismissedKey$
    ]).pipe(
      map(([status, subscription, user, url, dismissedKey]) => {
        this.currentStatus = status;
        this.currentSubscription = subscription;
        this.currentUser = user;
        const viewModel = this.buildViewModel(status, subscription, user, url, dismissedKey);
        this.syncShellOffset(viewModel.show);
        return viewModel;
      }),
      takeUntil(this.destroy$)
    );
  }

  ngOnDestroy(): void {
    this.syncShellOffset(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The routed page fills the shell, so a banner left in normal flow is painted
   * underneath it. Shift the outlet down by the rendered banner height.
   */
  private syncShellOffset(show: boolean): void {
    this.shellBannerShown = show;
    if (typeof document === 'undefined') {
      return;
    }
    const app = document.querySelector('ion-app');
    const host = document.querySelector('app-billing-enforcement-banner');
    if (!(app instanceof HTMLElement)) {
      return;
    }
    if (!show || !(host instanceof HTMLElement)) {
      this.clearShellOffset(app);
      return;
    }
    const measure = (attempt: number) => {
      if (!this.shellBannerShown) {
        this.clearShellOffset(app);
        return;
      }
      const height = host.offsetHeight;
      if (height <= 0 && attempt < 3) {
        this.scheduleShellMeasure(() => measure(attempt + 1));
        return;
      }
      if (height <= 0) {
        this.clearShellOffset(app);
        return;
      }
      app.style.setProperty('--solo-enforcement-banner-height', `${height}px`);
      app.classList.add('solo-enforcement-banner-open');
    };
    this.scheduleShellMeasure(() => measure(0));
  }

  private scheduleShellMeasure(measure: () => void): void {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(measure);
      return;
    }
    measure();
  }

  private clearShellOffset(app: HTMLElement): void {
    app.classList.remove('solo-enforcement-banner-open');
    app.style.removeProperty('--solo-enforcement-banner-height');
  }

  private buildViewModel(
    status: EnforcementStatus,
    subscription: Subscription | null,
    user: CurrentUser | null,
    url: string,
    dismissedKey: string | null
  ): BannerViewModel {
    const baseHidden: BannerViewModel = {
      show: false,
      level: 'past_due',
      severity: 'yellow',
      dismissible: false,
      deadlineLabel: '',
      daysLabel: '',
      isAdmin: true,
      pastDueBody: ''
    };

    if (status.level === 'none') {
      return baseHidden;
    }
    if (this.isSuppressedRoute(url)) {
      return baseHidden;
    }

    const severity = status.severity ?? (status.level === 'restricted' ? 'red' : 'yellow');
    // Restricted banners are never dismissible. Past-due banners are
    // dismissible per session, but a new severity ramp re-keys the dismiss so
    // the banner re-surfaces on escalation.
    const dismissible = status.level === 'past_due';
    if (dismissible && dismissedKey !== null) {
      const expectedKey = this.computeDismissKey(subscription, severity);
      if (expectedKey !== null && expectedKey === dismissedKey) {
        return baseHidden;
      }
    }

    // currentUser can be briefly null during initial load — default to admin
    // copy so we don't flash "Contact your admin" then swap to admin copy on
    // first emit. The first emit usually arrives synchronously via BehaviorSubject.
    const isAdmin = user === null ? true : user.canManageBilling;

    const daysRemaining = status.daysRemaining ?? 0;
    const deadlineLabel = this.formatDeadline(status.gracePeriodEndsAt ?? null);
    const daysLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
    const pastDueBody = this.getBannerCopy(severity, daysRemaining, deadlineLabel);

    return {
      show: true,
      level: status.level === 'restricted' ? 'restricted' : 'past_due',
      severity,
      dismissible,
      deadlineLabel,
      daysLabel,
      isAdmin,
      pastDueBody
    };
  }

  /**
   * Returns severity-specific past-due body copy. Built once per view-model
   * computation so the template binding receives a stable string reference
   * (no helper calls during change detection).
   *
   * Copy matches the spec (`plans/saas-billing/06-payment-enforcement-dunning.md`,
   * "App shell banner" sections for yellow/orange/red).
   */
  private getBannerCopy(
    severity: 'yellow' | 'orange' | 'red',
    daysRemaining: number,
    deadlineLabel: string
  ): string {
    const daysLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
    switch (severity) {
      case 'yellow':
        return `Update your payment method by ${deadlineLabel} to keep full access.`;
      case 'orange':
        return `Payment failed. ${daysLabel} left to update before access is restricted.`;
      case 'red':
        return `URGENT: AI chat will be disabled in ${daysLabel}. Update your payment method now to avoid losing access.`;
    }
  }

  private isSuppressedRoute(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    if (path === '' || path === '/') {
      return false;
    }
    return BANNER_SUPPRESS_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  }

  private computeDismissKey(
    subscription: Subscription | null,
    severity: 'yellow' | 'orange' | 'red'
  ): string | null {
    if (subscription === null) {
      return null;
    }
    const tenant = subscription.tenantId ?? 'unknown';
    const grace = subscription.gracePeriodEndsAt ?? 'none';
    return `${tenant}:${grace}:${severity}`;
  }

  private formatDeadline(iso: string | null): string {
    if (iso === null) {
      return 'soon';
    }
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  onCtaClick(): void {
    this.logger.info(
      'EnforcementBannerComponent',
      'onCtaClick',
      'opening Stripe Customer Portal session from app-shell banner',
      { level: this.currentStatus?.level, severity: this.currentStatus?.severity }
    );
    this.billing.createPortalSession('/billing')
      .pipe(takeUntil(this.destroy$))
      .subscribe(link => {
        window.open(link.url, '_blank', 'noopener');
      });
  }

  onDismiss(): void {
    const sub = this.currentSubscription;
    const status = this.currentStatus;
    if (sub === null || status === null || status.level !== 'past_due') {
      return;
    }
    const severity = status.severity ?? 'yellow';
    const key = this.computeDismissKey(sub, severity);
    if (key === null) {
      return;
    }
    this.persistDismiss(key);
    this.dismissedKey$.next(key);
    this.logger.info(
      'EnforcementBannerComponent',
      'onDismiss',
      'app-shell enforcement banner dismissed for the session',
      { key }
    );
  }

  private persistDismiss(key: string): void {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`billing.enforcement.dismissed`, key);
      }
    } catch (error) {
      // sessionStorage may be unavailable (private browsing). Banner still
      // dismisses for the current navigation since dismissedKey$ also tracks.
      this.logger.error(
        'EnforcementBannerComponent',
        'persistDismiss',
        'failed to write dismiss key to sessionStorage',
        { error }
      );
    }
  }
}
